import React, { useState, useEffect } from 'react';
import { NotebookText } from 'lucide-react';
import { toast } from 'sonner';
import { bookmarksAPI } from '@/api/bookmarks';
import { Button } from '@/components/ui/button';

export default function BookmarkIcon({
    chapterId,
    sectionName,
    sectionNumber,
    bookmarkType,
    contentId,
    contentData,
    initialBookmarked = false,
    bookmarkId = null,
    onToggle = null,
    className = "",
    size = 20
}) {
    const [isBookmarked, setIsBookmarked] = useState(initialBookmarked);
    const [currentBookmarkId, setCurrentBookmarkId] = useState(bookmarkId);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setIsBookmarked(initialBookmarked);
        setCurrentBookmarkId(bookmarkId);
    }, [initialBookmarked, bookmarkId]);

    const handleToggle = async (e) => {
        e.stopPropagation(); // Prevent triggering parent clicks (like video player controls)

        if (loading || !chapterId) return;

        // Optimistic Update
        const previousState = isBookmarked;
        setIsBookmarked(!previousState);
        setLoading(true);

        try {
            if (!previousState) {
                // Create Bookmark
                const payload = {
                    chapter_id: chapterId,
                    section_name: sectionName || "General",
                    section_number: sectionNumber,
                    bookmark_type: bookmarkType,
                    content_id: contentId ? String(contentId) : "",
                    content_data: contentData || {}
                };

                const newBookmark = await bookmarksAPI.createBookmark(payload);
                setCurrentBookmarkId(newBookmark.id);
                toast.success("Saved to Revision");
                if (onToggle) onToggle(true, newBookmark);
            } else {
                // Delete Bookmark
                if (currentBookmarkId) {
                    await bookmarksAPI.deleteBookmark(currentBookmarkId);
                    setCurrentBookmarkId(null);
                    toast.success("Removed from Revision");
                    if (onToggle) onToggle(false, null);
                }
            }
        } catch (error) {
            // Revert on failure
            setIsBookmarked(previousState);
            toast.error("Failed to update bookmark");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            variant="ghost"
            size="icon"
            className={`hover:bg-transparent ${className}`}
            onClick={handleToggle}
            disabled={loading}
        >
            <NotebookText
                size={size}
                className={`transition-all duration-200 ${isBookmarked
                    ? "fill-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                    : "text-muted-foreground hover:text-foreground"
                    }`}
            />
        </Button>
    );
}

